// Détail d'un ticket : en-tête et gestes, étapes, carte « Mise en
// production », bandeau d'échec, ticket, livraison, contenu d'origine,
// exécution, chronologie.
import type { ComponentChildren, JSX } from 'preact'
import { useState } from 'preact/hooks'
import type { TowerTicketDetail } from '@feedbaacks/sdk'
import { Button, cx, IconButton, Icons, useFeedbaacks } from '@feedbaacks/ui'
import { href, onLinkClick } from '../router'
import {
  FEEDBACK_STATUS, SEVERITY_META, STATE_META, TYPE_LABEL, durationLabel, eventDetails, failureReason, isActive, money, relative, shortTime,
  showApprovalCard, sourceLabel, type EventDetail,
} from './tower-model'
import { Chip, Fold, Section, TowerApprovalCard, TowerProgress, UntrustedOriginal } from './TowerParts'
import { useTowerAct } from './tower-hooks'
import { TowerNotes } from './TowerNotes'
import { TowerDemand } from './TowerDemand'

function Field({ label, children }: { label: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="tw-field">
      <span class="fb-muted">{label}</span>
      <span>{children}</span>
    </div>
  )
}

/** Un détail d'événement : en ligne s'il est court, à déplier sinon. */
function Detail({ d, open }: { d: EventDetail; open?: boolean }): JSX.Element {
  if (!d.long) return <span class="tw-ev-detail fb-muted">{d.label} : {d.text}</span>
  return (
    <details class="tw-ev-detail" open={open}>
      <summary class="fb-muted">{d.label}</summary>
      <pre class="tw-ev-pre">{d.text}</pre>
    </details>
  )
}

/** Une ligne de « Livraison » : fait, en attente, ou sans objet. */
function Proof({ ok, label, children }: { ok: boolean; label: string; children?: ComponentChildren }): JSX.Element {
  return (
    <li class={cx(!ok && 'is-pending')}>
      {ok ? <Icons.Check size={14} class="tw-ok" label="fait" /> : <span class="tw-pending-dot" aria-label="en attente" />}
      <span class="tw-proof-label">{label}</span>
      <span class="tw-proof-value">{children}</span>
    </li>
  )
}

export function TicketDetail({ ticket, isApprover, onChanged, onClose, onArchived }: {
  ticket: TowerTicketDetail
  isApprover: boolean
  onChanged: () => void
  onClose: () => void
  /** Après l'archivage depuis ce panneau (la liste passe au suivant). */
  onArchived?: () => void
}): JSX.Element {
  const { notify } = useFeedbaacks()
  const { busy, approve, sendBack, retry, relaunchDelivered, stop, archive } = useTowerAct(onChanged)
  const st = STATE_META[ticket.state]
  const itemHref = ticket.app ? href({ name: 'feedback', app: ticket.app, itemId: ticket.feedback_id }) : ''
  // Un ticket retiré de sa demande ne se relance pas : on en ajoute un nouveau.
  const failed = (ticket.state === 'failed' || ticket.state === 'stopped') && !ticket.removed
  const why = failed ? failureReason(ticket.events) : undefined
  const [fresh, setFresh] = useState(false)
  const running = isActive(ticket.state) || ticket.state === 'awaiting_approval'
  const lastLine = ticket.jobs.find((j) => j.state === 'running' || j.state === 'claimed')?.last_line
  const copyBranch = () => navigator.clipboard?.writeText(ticket.branch).then(() => notify('success', 'Branche copiée.'), () => {})

  return (
    <div class="tw-detail" data-testid="tower-ticket-detail">
      <header class="tw-detail-head">
        <div class="tw-row">
          <code class="fb-mono tw-code">{ticket.code}</code>
          <Chip tone={ticket.removed ? undefined : st.tone} testId="tower-ticket-state">{ticket.removed ? 'Retiré' : st.label}</Chip>
          <Chip>{TYPE_LABEL[ticket.type]}</Chip>
          {ticket.archived_at && <Chip testId="tower-ticket-archived"><Icons.Archive size={11} />Archivé {relative(ticket.archived_at)}</Chip>}
          <span class="tw-push"><IconButton label="Fermer le détail (Échap)" onClick={onClose}><Icons.X size={16} /></IconButton></span>
        </div>
        <h2>{ticket.title}</h2>
        <div class="tw-row fb-muted tw-small">
          <span class="fb-mono">{ticket.project}</span>
          <span aria-hidden="true">·</span>
          <span>{sourceLabel(ticket.source, ticket.company)}</span>
          <span aria-hidden="true">·</span>
          <span title={new Date(ticket.created_at).toLocaleString('fr-FR')}>créé {relative(ticket.created_at)}</span>
          <span aria-hidden="true">·</span>
          <span>côté auteur : {FEEDBACK_STATUS[ticket.feedback_status] ?? ticket.feedback_status}</span>
        </div>
        <div class="tw-detail-actions">
          {ticket.pr_url && (
            <a class="fb-btn fb-btn-secondary" href={ticket.pr_url} target="_blank" rel="noreferrer" data-testid="tower-open-pr">
              PR #{ticket.pr_number}<Icons.External size={12} />
            </a>
          )}
          {ticket.preview_url && (
            <a class="fb-btn fb-btn-secondary" href={ticket.preview_url} target="_blank" rel="noreferrer">Preview<Icons.External size={12} /></a>
          )}
          {itemHref && <a class="fb-btn fb-btn-ghost" href={itemHref} onClick={(e) => onLinkClick(e, itemHref)}><Icons.Message size={14} />Retour d'origine</a>}
          <span class="tw-push tw-row">
            {running && (
              <Button variant="ghost" disabled={busy} onClick={() => void stop(ticket)} testId="tower-stop-button"><Icons.X size={14} />Arrêter</Button>
            )}
            {ticket.can_archive && (ticket.archived_at
              ? <Button disabled={busy} onClick={() => void archive(ticket, false)} testId="tower-unarchive-button"><Icons.ArchiveRestore size={14} />Sortir des archives</Button>
              : <Button disabled={busy} onClick={() => void archive(ticket, true).then((ok) => ok && onArchived?.())} testId="tower-archive-button"><Icons.Archive size={14} />Archiver</Button>)}
          </span>
        </div>
      </header>

      <TowerProgress state={ticket.state} stage={ticket.stage} labeled />

      {showApprovalCard(ticket.state) && (
        <TowerApprovalCard checks={ticket.checks} headSha={ticket.head_sha} defaultBranch={ticket.default_branch}
          previewUrl={ticket.preview_url} expiresAt={ticket.card_expires_at} canApprove={ticket.can_approve}
          isApprover={isApprover} busy={busy} onApprove={() => approve(ticket, (url) => window.open(url, '_blank', 'noopener'))}
          onSendBack={(note) => sendBack(ticket.id, note)} />
      )}

      {failed && (
        <div class="tw-banner is-danger" data-testid="tower-failure-banner">
          <span>{ticket.error || 'Le ticket s\'est arrêté.'}</span>
          {why && <div class="tw-banner-why" data-testid="tower-failure-why"><Detail d={why} open /></div>}
          <div class="tw-banner-actions">
            <label class="tw-check fb-muted" title="Sinon l'agent reprend sa branche, rebasée, avec les remarques de l'équipe">
              <input type="checkbox" checked={fresh} onChange={(e) => setFresh((e.target as HTMLInputElement).checked)} data-testid="tower-retry-fresh" />
              Repartir de zéro sur {ticket.default_branch}
            </label>
            <Button disabled={busy} onClick={() => retry(ticket.id, fresh)} testId="tower-retry-button">
              <Icons.Refresh size={14} />Relancer
            </Button>
          </div>
        </div>
      )}

      {ticket.removed && (
        <div class="tw-banner is-info" data-testid="tower-removed-banner">
          <span>Retiré de la demande par l'équipe : il ne part plus et n'est plus attendu. Pour ce projet, ajoutez un nouveau ticket à la demande.</span>
        </div>
      )}

      {ticket.waiting && (
        <div class="tw-banner is-info" data-testid="tower-waiting-banner">
          <span>En attente : {ticket.waiting}. Il part dès que chaque ticket amont est livré et servi en staging ; son agent reçoit alors le contexte des tickets amont.</span>
        </div>
      )}

      {lastLine && <p class="tw-now fb-mono" title="Dernière ligne de l'agent">{lastLine}</p>}

      <Section title="Le ticket">
        <p class="tw-summary">{ticket.summary}</p>
        {ticket.criteria.length > 0 && (
          <ul class="tw-criteria">
            {ticket.criteria.map((c) => (
              <li key={c}>
                {ticket.state === 'deployed' ? <Icons.Check size={14} class="tw-ok" label="fait" /> : <span class="tw-criterion-dot" />}
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
        <div class="tw-grid">
          <Field label="Gravité"><Chip tone={SEVERITY_META[ticket.severity].tone}>{SEVERITY_META[ticket.severity].label}</Chip></Field>
          <Field label="Confiance du tri">{Math.round(ticket.confidence * 100)} %</Field>
          <Field label="Budget">{money(ticket.spent_usd)} sur {money(ticket.budget_usd)}</Field>
          <Field label="Autonomie">{ticket.autonomy}</Field>
          <Field label="Branche">
            <button type="button" class="tw-copy fb-mono" title="Copier la branche" onClick={copyBranch}>
              <span class="tw-truncate">{ticket.branch}</span><Icons.Copy size={12} />
            </button>
          </Field>
        </div>
      </Section>

      <Section title="Livraison">
        <ul class="tw-proofs">
          <Proof ok={ticket.ci_ok} label="CI chez nous">{ticket.ci_ok ? `verte · ${ticket.ci_tests ?? 0} tests` : 'pas encore verte'}</Proof>
          <Proof ok={ticket.review_ok} label="Revue croisée">
            {ticket.review_ok ? 'approuvée' : 'en attente'}
            {ticket.review_summary && <span class="tw-review fb-muted">{ticket.review_summary}</span>}
          </Proof>
          <Proof ok={!!ticket.preview_url} label="Preview">
            {ticket.preview_url
              ? <a href={ticket.preview_url} target="_blank" rel="noreferrer" class="fb-mono">{ticket.preview_url.replace(/^https:\/\//, '')}</a>
              : 'pas encore en ligne'}
          </Proof>
          <Proof ok={!!ticket.pr_url} label="Pull request">
            {ticket.pr_url
              ? <a href={ticket.pr_url} target="_blank" rel="noreferrer" class="fb-mono">#{ticket.pr_number} sur {ticket.default_branch}</a>
              : 'ouverte à l\'approbation'}
          </Proof>
        </ul>
      </Section>

      <TowerDemand ticket={ticket} onChanged={onChanged} />

      <TowerNotes ticket={ticket} onChanged={onChanged}
        relaunch={ticket.state === 'deployed' ? { busy, onClick: () => relaunchDelivered(ticket) } : undefined} />

      <Fold title="Contenu d'origine" open={!ticket.archived_at && ticket.state !== 'deployed'}>
        <UntrustedOriginal original={ticket.original} />
      </Fold>

      <Fold title="Exécution" open={isActive(ticket.state)} aside={`${ticket.jobs.length} Job${ticket.jobs.length > 1 ? 's' : ''}`}>
        {ticket.jobs.length === 0 ? (
          <p class="fb-muted">Aucun Job lancé pour l'instant : le ticket attend une place libre.</p>
        ) : (
          <div class="tw-table-wrap">
            <table class="tw-table">
              <thead><tr><th>Étape</th><th>Modèle</th><th>État</th><th>Durée</th><th>Coût</th><th>Détail</th></tr></thead>
              <tbody>
                {ticket.jobs.map((j) => {
                  const secs = j.started_at ? ((j.finished_at ? new Date(j.finished_at) : new Date()).getTime() - new Date(j.started_at).getTime()) / 1000 : 0
                  return (
                    <tr key={j.id}>
                      <td class="fb-mono">{j.stage}</td>
                      <td class="fb-mono fb-muted">{j.model || 'aucun'}</td>
                      <td>{j.state}</td>
                      <td>{j.started_at ? durationLabel(secs) : ''}</td>
                      <td>{money(j.cost_usd)}</td>
                      <td class="tw-truncate fb-muted" title={j.last_line}>{j.last_line}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Fold>

      <Fold title="Chronologie" open aside={ticket.events.length ? `${ticket.events.length} événements` : undefined}>
        <ol class="tw-timeline">
          {ticket.events.map((e, i) => (
            <li key={i} class={cx(`is-${e.status}`)}>
              <time dateTime={e.at} title={new Date(e.at).toLocaleString('fr-FR')} class="fb-mono fb-muted">{shortTime(e.at)}</time>
              <div class="tw-ev">
                <span class={cx(e.status === 'anomaly' && 'tw-anomaly', e.status === 'error' && 'tw-ko')}>{e.message}</span>
                {eventDetails(e).map((d) => <Detail key={d.label} d={d} />)}
              </div>
            </li>
          ))}
        </ol>
      </Fold>
    </div>
  )
}
